import { makeSolved } from './makeSolved'
import { neighborsOfEmpty } from './neighborsOfEmpty'

/**
 * Shuffles puzzle board using random walk algorithm
 *
 * Creates a solvable shuffled board by starting from solved state
 * and applying K legal moves. Uses no-backtrack strategy to avoid
 * undoing the previous move immediately.
 *
 * Adds a small "jiggle" phase (4 extra moves) to avoid trivial
 * 1-2 move solutions that can occur with low shuffle depths.
 *
 * @param {number} size - Board dimension (e.g., 3 for 3x3)
 * @param {number} stepsK - Number of shuffle moves to apply
 * @param {function} [rng=Math.random] - Random number generator (for testing)
 * @returns {number[]} Shuffled board array (always solvable)
 *
 * @example
 * // Basic usage
 * randomWalkShuffle(3, 50)  // Returns shuffled 3x3 board
 *
 * // With seeded RNG for deterministic testing
 * const seededRng = () => 0.5
 * randomWalkShuffle(3, 10, seededRng)  // Reproducible shuffle
 *
 * @remarks
 * - Always produces solvable boards (starts from solved, legal moves only)
 * - No immediate backtracking (previous position filtered from options)
 * - Includes defensive fallback if options[] ever empty (N>=2 should never happen)
 * - JIGGLE_STEPS = 4 balances simplicity vs avoiding trivial solutions
 */
export function randomWalkShuffle (size, stepsK, rng = Math.random) {
  const emptyValue = size * size - 1
  const board = makeSolved(size)
  let emptyIdx = board.indexOf(emptyValue) // initially last index
  let prevEmpty = -1

  // Main depth walk
  for (let k = 0; k < stepsK; k++) {
    let options = neighborsOfEmpty(emptyIdx, size).filter(i => i !== prevEmpty)
    if (options.length === 0) options = neighborsOfEmpty(emptyIdx, size) // defensive fallback
    const choice = options[Math.floor(rng() * options.length)]
    const tmp = board[choice]
    board[choice] = board[emptyIdx]
    board[emptyIdx] = tmp
    prevEmpty = emptyIdx
    emptyIdx = choice
  }

  // Small anti-trivial jiggle (constant, no loops/heuristics)
  const JIGGLE_STEPS = 4 // chosen to balance simplicity vs avoiding 1–2 move solutions
  for (let s = 0; s < JIGGLE_STEPS; s++) {
    let options = neighborsOfEmpty(emptyIdx, size).filter(i => i !== prevEmpty)
    if (options.length === 0) options = neighborsOfEmpty(emptyIdx, size) // defensive fallback
    const choice = options[Math.floor(rng() * options.length)]
    const tmp = board[choice]
    board[choice] = board[emptyIdx]
    board[emptyIdx] = tmp
    prevEmpty = emptyIdx
    emptyIdx = choice
  }

  return board
}
